import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  forwardRef,
  Injector,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString
} from 'libphonenumber-js';
import { Subscription } from 'rxjs';
import { RxUtil } from 'utility/rx.util';

interface CountryOption {
  code: CountryCode;
  name: string;
  callingCode: string;
  flag: string;
}

const DEFAULT_COUNTRY: CountryCode = 'US';

/**
 * A country-aware phone number input. Renders a country selector (defaulting to
 * the location's country) alongside a national-number field, and exposes a
 * single canonical E.164 value to the surrounding reactive form. The selector
 * lets guests whose number belongs to a different country than the location
 * (e.g. travellers) enter a valid number without typing a `+` prefix.
 */
@Component({
  selector: 'v2-phone-input',
  templateUrl: 'v2-phone-input.component.html',
  styleUrls: ['v2-phone-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => V2PhoneInput),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => V2PhoneInput),
      multi: true
    }
  ]
})
export class V2PhoneInput
  implements ControlValueAccessor, Validator, OnInit, OnDestroy, DoCheck
{
  @Input() labelText = 'Phone Number';
  @Input() placeholder = '';
  @Input() required = false;

  /** ISO country to default the selector to (e.g. the location's countryCode). */
  @Input() set defaultCountry(code: string | null | undefined) {
    const normalized = (code?.toUpperCase() ?? '') as CountryCode;
    this._defaultCountry = this.isKnownCountry(normalized)
      ? normalized
      : DEFAULT_COUNTRY;
    if (!this.countrySelectedByUser) {
      this.selectedCountry = this._defaultCountry;
    }
  }
  private _defaultCountry: CountryCode = DEFAULT_COUNTRY;

  readonly countries: CountryOption[] = this.buildCountryOptions();

  selectedCountry: CountryCode = DEFAULT_COUNTRY;
  readonly nationalControl = new FormControl('');

  private countrySelectedByUser = false;
  private valueSub?: Subscription;
  private ngControl?: NgControl;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private injector: Injector
  ) {}

  ngOnInit(): void {
    // Resolve the bound NgControl lazily to avoid a circular dependency with
    // the NG_VALUE_ACCESSOR this component provides.
    this.ngControl = this.injector.get(NgControl, null) ?? undefined;
    this.nationalControl.setValidators(this.buildNationalValidators());
    this.valueSub = this.nationalControl.valueChanges.subscribe(() => {
      this.onChange(this.currentE164());
      this.onValidatorChange();
    });
  }

  ngDoCheck(): void {
    // Mirror the outer control's touched state onto the inner control so the
    // form field shows validation errors when the page marks the form touched
    // (e.g. on a submit attempt).
    if (this.ngControl?.touched && !this.nationalControl.touched) {
      this.nationalControl.markAsTouched();
      this.changeDetectorRef.markForCheck();
    }
  }

  ngOnDestroy(): void {
    RxUtil.cleanupSubscription(this.valueSub);
  }

  // --- ControlValueAccessor ---

  writeValue(value: string | null | undefined): void {
    if (value) {
      // Existing values may be E.164 (newer) or a legacy national number.
      const parsed = parsePhoneNumberFromString(value, this._defaultCountry);
      if (parsed) {
        if (parsed.country) this.selectedCountry = parsed.country;
        this.nationalControl.setValue(parsed.formatNational(), {
          emitEvent: false
        });
      } else {
        this.nationalControl.setValue(value, { emitEvent: false });
      }
    } else {
      this.nationalControl.setValue('', { emitEvent: false });
    }
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.nationalControl.disable({ emitEvent: false });
    } else {
      this.nationalControl.enable({ emitEvent: false });
    }
  }

  // --- Validator ---

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.nationalControl.errors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  // --- template handlers ---

  onCountryChange(code: CountryCode): void {
    this.selectedCountry = code;
    this.countrySelectedByUser = true;
    // Re-run validation/formatting of the existing input under the new country.
    const raw = this.nationalControl.value ?? '';
    this.nationalControl.setValue(new AsYouType(code).input(raw));
    this.nationalControl.markAsTouched();
    this.onTouched();
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    // Only format once there is enough input that the user can still backspace
    // past punctuation the formatter inserts.
    if (target.value.length > 4) {
      target.value = new AsYouType(this.selectedCountry).input(target.value);
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  get errorMessage(): string {
    if (this.nationalControl.hasError('required')) {
      return 'This field is required.';
    }
    return 'Please provide a valid phone number.';
  }

  private currentE164(): string {
    const raw = this.nationalControl.value?.trim();
    if (!raw) return '';
    const parsed = parsePhoneNumberFromString(raw, this.selectedCountry);
    // When valid, emit canonical E.164; otherwise pass the raw text through so
    // `required` still sees a value (format validity is reported separately).
    return parsed?.isValid() ? parsed.number : raw;
  }

  private buildNationalValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = [this.phoneValidator];
    if (this.required) validators.push(Validators.required);
    return validators;
  }

  private phoneValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const raw = control.value?.trim();
    if (!raw) return null;
    const parsed = parsePhoneNumberFromString(raw, this.selectedCountry);
    return parsed?.isValid() ? null : { phone: true };
  };

  private isKnownCountry(code: string): code is CountryCode {
    return !!code && (getCountries() as string[]).includes(code);
  }

  private buildCountryOptions(): CountryOption[] {
    const DisplayNames = (Intl as any).DisplayNames;
    let regionNames: { of(code: string): string | undefined } | undefined;
    try {
      regionNames = DisplayNames
        ? new DisplayNames(['en'], { type: 'region' })
        : undefined;
    } catch {
      regionNames = undefined;
    }

    return getCountries()
      .map((code) => ({
        code,
        name: regionNames?.of(code) ?? code,
        callingCode: getCountryCallingCode(code),
        flag: this.toFlagEmoji(code)
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private toFlagEmoji(code: string): string {
    return code
      .toUpperCase()
      .replace(/./g, (char) =>
        String.fromCodePoint(127397 + char.charCodeAt(0))
      );
  }
}
